// Audit Log Types for WTF Amplify CRM

export interface AuditLogUser {
  id: string;
  name: string;
  email: string;
}

export interface AuditLogChange {
  old: any;
  new: any;
}

export interface AuditLog {
  id: string;
  type: string;
  description: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  user?: AuditLogUser;
  userId?: string;
  changes?: Record<string, AuditLogChange>;
  metadata?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface AuditLogFilters {
  page?: number;
  limit?: number;
  userId?: string;
  entityType?: string;
  entityId?: string;
  types?: string[];
  action?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface AuditLogPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface AuditLogsResponse {
  logs: AuditLog[];
  pagination: AuditLogPagination;
}

// Activity type definitions for icons and colors
export interface ActivityTypeConfig {
  type: string;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
}

export const ACTIVITY_TYPE_CONFIGS: Record<string, ActivityTypeConfig> = {
  // Lead activities
  LEAD_CREATED: {
    type: 'LEAD_CREATED',
    label: 'Lead Created',
    icon: 'Plus',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  LEAD_UPDATED: {
    type: 'LEAD_UPDATED',
    label: 'Lead Updated',
    icon: 'Edit',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  LEAD_DELETED: {
    type: 'LEAD_DELETED',
    label: 'Lead Deleted',
    icon: 'Trash2',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  STAGE_CHANGE: {
    type: 'STAGE_CHANGE',
    label: 'Stage Changed',
    icon: 'ArrowRight',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  PRIORITY_CHANGE: {
    type: 'PRIORITY_CHANGE',
    label: 'Priority Changed',
    icon: 'Flag',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  ASSIGNMENT_CHANGE: {
    type: 'ASSIGNMENT_CHANGE',
    label: 'Assignment Changed',
    icon: 'UserPlus',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  // Task activities
  TASK_CREATED: {
    type: 'TASK_CREATED',
    label: 'Task Created',
    icon: 'CheckSquare',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  TASK_COMPLETED: {
    type: 'TASK_COMPLETED',
    label: 'Task Completed',
    icon: 'CheckCircle',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  TASK_UPDATED: {
    type: 'TASK_UPDATED',
    label: 'Task Updated',
    icon: 'Edit',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  TASK_DELETED: {
    type: 'TASK_DELETED',
    label: 'Task Deleted',
    icon: 'Trash2',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  // User activities
  USER_LOGIN: {
    type: 'USER_LOGIN',
    label: 'User Login',
    icon: 'LogIn',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  USER_LOGOUT: {
    type: 'USER_LOGOUT',
    label: 'User Logout',
    icon: 'LogOut',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
  },
  USER_CREATED: {
    type: 'USER_CREATED',
    label: 'User Created',
    icon: 'UserPlus',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  USER_UPDATED: {
    type: 'USER_UPDATED',
    label: 'User Updated',
    icon: 'User',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  // Note and file activities
  NOTE_ADDED: {
    type: 'NOTE_ADDED',
    label: 'Note Added',
    icon: 'FileText',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
  },
  FILE_UPLOADED: {
    type: 'FILE_UPLOADED',
    label: 'File Uploaded',
    icon: 'Upload',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100',
  },
  // Communication activities
  EMAIL_SENT: {
    type: 'EMAIL_SENT',
    label: 'Email Sent',
    icon: 'Mail',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  CALL_MADE: {
    type: 'CALL_MADE',
    label: 'Call Made',
    icon: 'Phone',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  WHATSAPP_SENT: {
    type: 'WHATSAPP_SENT',
    label: 'WhatsApp Sent',
    icon: 'MessageSquare',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  // Generic fallback
  DEFAULT: {
    type: 'DEFAULT',
    label: 'Activity',
    icon: 'Activity',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
  },
};

// Entity type definitions
export const ENTITY_TYPES = [
  { value: 'Lead', label: 'Leads' },
  { value: 'Task', label: 'Tasks' },
  { value: 'User', label: 'Users' },
  { value: 'Note', label: 'Notes' },
  { value: 'File', label: 'Files' },
  { value: 'Proposal', label: 'Proposals' },
];

// Activity type options for filtering
export const ACTIVITY_TYPE_OPTIONS = [
  { value: 'LEAD_CREATED', label: 'Lead Created' },
  { value: 'LEAD_UPDATED', label: 'Lead Updated' },
  { value: 'LEAD_DELETED', label: 'Lead Deleted' },
  { value: 'STAGE_CHANGE', label: 'Stage Changed' },
  { value: 'PRIORITY_CHANGE', label: 'Priority Changed' },
  { value: 'ASSIGNMENT_CHANGE', label: 'Assignment Changed' },
  { value: 'TASK_CREATED', label: 'Task Created' },
  { value: 'TASK_COMPLETED', label: 'Task Completed' },
  { value: 'TASK_UPDATED', label: 'Task Updated' },
  { value: 'TASK_DELETED', label: 'Task Deleted' },
  { value: 'USER_LOGIN', label: 'User Login' },
  { value: 'USER_LOGOUT', label: 'User Logout' },
  { value: 'NOTE_ADDED', label: 'Note Added' },
  { value: 'FILE_UPLOADED', label: 'File Uploaded' },
  { value: 'EMAIL_SENT', label: 'Email Sent' },
  { value: 'CALL_MADE', label: 'Call Made' },
  { value: 'WHATSAPP_SENT', label: 'WhatsApp Sent' },
];
